/**
 * @module lib/print/PrintService
 * @description Prints any set of card faces on any device's printer, one
 * face per sheet, at true postcard size.
 *
 * How it works: `print(job)` mounts the pages into a dedicated print root
 * (outside the app's layout), sets the page size with a generated
 * `@page { size: W H; margin: 0 }` rule, waits for fonts and photos, then
 * opens the browser's print dialog. print.css hides everything else. The
 * root is cleared after the dialog closes.
 *
 * Why this replaced the 1A printElement(): 1A printed a single on-screen
 * card at a fixed 9 × 6 in page, which is not a postcard size most printers
 * or shops expect (Samantha's feedback), and could not print a back.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { PrintSize } from "../../config/print";
import { waitForImages } from "../outputEngine";
import { FixedLandscape } from "../../app/cardOrientation";

/** One sheet: a card face. */
export interface PrintPage {
    key: string;
    node: ReactNode;
}

export interface PrintJob {
    pages: PrintPage[];
    size: PrintSize;
}

type PrintFn = (job: PrintJob) => void;

const PrintContext = createContext<PrintFn | null>(null);

/** Access to the shared print service. Must be used under <PrintProvider>. */
export function usePrint(): PrintFn {
    const print = useContext(PrintContext);
    if (!print) throw new Error("usePrint() must be used inside <PrintProvider>.");
    return print;
}

const PAGE_STYLE_ID = "afj-print-page-size";

/** Writes the @page rule for this job. A <style> element is the only way to set page size at runtime. */
function setPageSize(size: PrintSize): void {
    let style = document.getElementById(PAGE_STYLE_ID) as HTMLStyleElement | null;
    if (!style) {
        style = Object.assign(document.createElement("style"), { id: PAGE_STYLE_ID });
        document.head.appendChild(style);
    }
    style.textContent = `@page { size: ${size.widthIn}in ${size.heightIn}in; margin: 0; }
#print-root { --print-w: ${size.widthIn}in; --print-h: ${size.heightIn}in; }`;
}

export function PrintProvider({ children }: { children: ReactNode }) {
    const [job, setJob] = useState<PrintJob | null>(null);
    const rootRef = useRef<HTMLDivElement>(null);

    const print = useCallback<PrintFn>((next) => setJob(next), []);

    // Once the pages are in the DOM: size the page, wait for assets, open the dialog.
    useEffect(() => {
        if (!job) return;
        let cancelled = false;
        const cleanup = () => {
            document.body.classList.remove("is-printing");
            window.removeEventListener("afterprint", cleanup);
            setJob(null);
        };
        (async () => {
            setPageSize(job.size);
            await document.fonts.ready;
            if (rootRef.current) await waitForImages(rootRef.current);
            if (cancelled) return;
            document.body.classList.add("is-printing");
            window.addEventListener("afterprint", cleanup);
            window.print();
        })().catch((error: unknown) => {
            console.error("[PrintService] Print failed", error);
            cleanup();
        });
        return () => {
            cancelled = true;
        };
    }, [job]);

    return (
        <PrintContext.Provider value={print}>
            {children}
            {createPortal(
                <div id="print-root" ref={rootRef} aria-hidden="true">
                    {/* Printed cards are postcards: always the landscape layout, even when printing from a phone. */}
                    <FixedLandscape>
                        {job?.pages.map((page) => (
                            <div key={page.key} className="print-page">
                                {page.node}
                            </div>
                        ))}
                    </FixedLandscape>
                </div>,
                document.body
            )}
        </PrintContext.Provider>
    );
}
